import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CampaignDashboardMetrics {
  total_campaigns: number;
  active_campaigns: number;
  linkedin_campaigns: number;
  email_campaigns: number;
  active_linkedin_campaigns: number;
  active_email_campaigns: number;
  total_linkedin_leads: number;
  total_linkedin_sent: number;
  total_linkedin_accepted: number;
  total_linkedin_replied: number;
  total_linkedin_messages_sent: number;
  avg_linkedin_acceptance_rate: number;
  avg_linkedin_reply_rate: number;
  total_email_contacts: number;
  total_emails_sent: number;
  total_emails_opened: number;
  total_emails_clicked: number;
  total_emails_replied: number;
  total_emails_bounced: number;
  avg_email_open_rate: number;
  avg_email_reply_rate: number;
  unique_contacts_reached: number;
  total_touches: number;
  unique_accounts_touched: number;
  grand_total_contacts: number;
  grand_total_sent: number;
  grand_total_replies: number;
}

export interface ExecutivePerformance {
  persona_id: string;
  full_name: string;
  email: string | null;
  avatar_url: string | null;
  team: string | null;
  total_campaigns: number;
  active_campaigns: number;
  // Phase 8A: LI / Email split columns
  linkedin_sent: number;
  linkedin_accepted: number;
  linkedin_acceptance_rate: number | null;
  linkedin_replied: number;
  linkedin_messages_sent: number;
  email_sent: number;
  email_replied: number;
  // Back-compat aggregates (still emitted by view)
  total_sent: number;
  total_replied: number;
  reply_rate: number | null;
  last_campaign_activity: string | null;
}

export interface UnifiedCampaign {
  campaign_id: string;
  campaign_name: string;
  campaign_status: string | null;
  platform: string | null;
  territory: string | null;
  executive_name: string | null;
  linkedin_leads: number;
  linkedin_sent: number;
  linkedin_accepted: number;
  linkedin_replied: number;
  linkedin_messages_sent: number;
  linkedin_acc_rate: number | null;
  linkedin_reply_rate: number | null;
  email_contacts: number;
  email_sent: number;
  email_opened: number;
  email_clicked: number;
  email_replied: number;
  email_bounced: number;
  email_open_rate: number | null;
  email_reply_rate: number | null;
  total_contacts: number;
  total_sent: number;
  replies_count: number;
  created_at: string | null;
}

export interface UnifiedCampaignContact {
  engagement_id: string;
  campaign_id: string;
  campaign_name: string;
  campaign_platform: string | null;
  data_source: string | null;
  source_contact_id: string | null;
  master_contact_id: string | null;
  account_id: string | null;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  email: string | null;
  job_title: string | null;
  linkedin_url: string | null;
  company_name: string | null;
  account_name: string | null;
  account_domain: string | null;
  account_category: string | null;
  location: string | null;
  engagement_status: string | null;
  enrolled_at: string | null;
  sent_at: string | null;
  accepted_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  replied_at: string | null;
  bounced_at: string | null;
  unsubscribed_at: string | null;
  reply_sentiment: string | null;
  outcome: string | null;
  meeting_booked: boolean | null;
  messages_sent_count: number | null;
  current_sequence_step: number | null;
  // Phase 6A v3 additions
  connected_at_canonical: string | null;
  connected_via_sender_name: string | null;
  // Phase 2 (2026-06-04): handoff timestamp (email handoff_to_inbox_at) + row mtime
  handoff_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export const useCampaignDashboardMetrics = () => {
  return useQuery({
    queryKey: ['campaign-dashboard-metrics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaign_tracker_metrics' as any)
        .select('*')
        .single();

      if (error) {
        console.error('Error fetching campaign metrics:', error);
        throw error;
      }

      return (data || {}) as unknown as CampaignDashboardMetrics;
    },
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Phase 9 (2026-06-01) — HeyReach UI-truth headline metrics.
 *
 * Sources from `v_heyreach_overall_stats_total`, which rolls up daily snapshots
 * of HR's POST /stats/GetOverallStats endpoint — the SAME endpoint that powers
 * the HR UI dashboard. These numbers are guaranteed to match what users see
 * inside HeyReach (deduped, person-centric).
 *
 * Per-campaign-lead aggregates (in `campaign_tracker_metrics`) still exist for
 * drill-down, but the dashboard headline tiles should source from here.
 */
export interface HeyReachOverallTotals {
  connections_sent: number;
  connections_accepted: number;
  unique_leads_contacted: number;
  messages_sent: number;
  total_message_started: number;
  total_message_replies: number;
  acceptance_rate: number;     // 0..1
  reply_rate: number;          // 0..1
  first_day: string | null;
  last_day: string | null;
}

export const useHeyReachOverallTotals = () => {
  return useQuery({
    queryKey: ['heyreach-overall-totals'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_heyreach_overall_stats_total' as any)
        .select('*')
        .eq('scope', 'all')
        .maybeSingle();
      if (error) {
        console.error('Error fetching HR overall totals:', error);
        throw error;
      }
      return (data || null) as unknown as HeyReachOverallTotals | null;
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useExecutivePerformance = () => {
  return useQuery({
    queryKey: ['campaign-executive-performance'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaign_executive_performance' as any)
        .select('*')
        // Audit-2026-06-01: align server-side order with the UI default sort
        // (linkedin_accepted DESC). The CampaignHub exec table re-sorts client
        // side; this just removes a wasted alphabetical pass on the wire.
        .order('linkedin_accepted', { ascending: false, nullsFirst: false });

      if (error) {
        console.error('Error fetching executive performance:', error);
        throw error;
      }

      return (data || []) as unknown as ExecutivePerformance[];
    },
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Tiered target-pool penetration (Jake's "how far are we in?" ask, Jun 3 call).
 * One row per ICP tier (Tier 1/2/3) with total vs. contacted contacts. Soft-fails
 * to [] if the view isn't present yet.
 */
export interface TargetPoolTier {
  tier: string;
  total_contacts: number;
  total_accounts: number;
  contacted_contacts: number;
  remaining_contacts: number;
  penetration_pct: number;
  // Jul 6 (Brian): funnel-stage columns — Total → Filter-eligible → Persona → Deployed.
  filter_eligible_contacts: number;
  persona_contacts: number;
  deployed_persona_contacts: number;
  // Of persona_contacts, how many are the off-ICP sentinel (bulk-labeled).
  out_of_icp_contacts: number;
}

export const useTargetPool = () => {
  return useQuery({
    queryKey: ['campaign-target-pool'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_campaign_target_pool' as any)
        .select('*')
        .order('tier');
      if (error) return [] as TargetPoolTier[];
      return (data || []) as unknown as TargetPoolTier[];
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};

/**
 * Account-level target-pool funnel (Jul 6, Brian): one row per ICP tier with the
 * account funnel — total → w/ contacts → w/ filter-eligible → w/ persona → deployed.
 * Mirrors useTargetPool but counts accounts (companies) at each stage. Soft-fails to [].
 */
export interface TargetPoolAccountTier {
  tier: string;
  accounts_total: number;
  accounts_with_contacts: number;
  accounts_with_filter_eligible: number;
  accounts_with_persona: number;
  accounts_deployed: number;
  // Accounts whose only/any persona coverage is the off-ICP sentinel.
  accounts_out_of_icp: number;
}

export const useTargetPoolAccounts = () => {
  return useQuery({
    queryKey: ['campaign-target-pool-accounts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_campaign_target_pool_accounts' as any)
        .select('*')
        .order('tier');
      if (error) return [] as TargetPoolAccountTier[];
      return (data || []) as unknown as TargetPoolAccountTier[];
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};

export const useCampaignsUnified = (filters?: {
  status?: string;
  platform?: string;
  persona?: string;
  territory?: string;
}) => {
  return useQuery({
    queryKey: ['campaign-tracker-campaigns', filters],
    queryFn: async () => {
      let query = supabase
        .from('campaign_tracker_campaigns' as any)
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('campaign_status', filters.status);
      } else {
        query = query.neq('campaign_status', 'archived');
      }
      if (filters?.territory && filters.territory !== 'all') {
        query = query.eq('territory', filters.territory);
      }
      if (filters?.persona && filters.persona !== 'all') {
        // Audit-2026-06-01: previously included `executive_name.is.null`, leaking
        // unassigned campaigns into every persona-scoped view. Persona filter now
        // strict-matches.
        query = query.eq('executive_name', filters.persona);
      }
      if (filters?.platform && filters.platform !== 'all') {
        query = query.eq('platform', filters.platform);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching campaigns:', error);
        throw error;
      }

      return (data || []) as unknown as UnifiedCampaign[];
    },
    staleTime: 2 * 60 * 1000,
  });
};

export const useSendingPersonas = () => {
  return useQuery({
    queryKey: ['campaign-sending-personas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sending_personas')
        .select('id, full_name, email, team, is_active')
        .eq('is_active', true)
        .order('full_name');

      if (error) {
        console.error('Error fetching personas:', error);
        return [];
      }

      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useTerritories = () => {
  return useQuery({
    queryKey: ['campaign-territories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaign_territories')
        .select('name')
        .order('name');

      if (error) {
        console.error('Error fetching territories:', error);
        return [];
      }

      return (data || []).map((t) => t.name).filter(Boolean) as string[];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useCampaignContacts = (campaignId: string | null) => {
  return useQuery({
    queryKey: ['campaign-contacts', campaignId],
    enabled: !!campaignId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('unified_campaign_contacts' as any)
        .select('*')
        .eq('campaign_id', campaignId!)
        .order('full_name');

      if (error) {
        console.error('Error fetching campaign contacts:', error);
        throw error;
      }

      return (data || []) as unknown as UnifiedCampaignContact[];
    },
    staleTime: 2 * 60 * 1000,
  });
};

export const useArchiveCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (campaignId: string) => {
      const { error } = await supabase
        .from('campaigns')
        .update({ status: 'archived' })
        .eq('id', campaignId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-tracker-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-dashboard-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['list-builder-campaigns'] });
    },
  });
};

export type CampaignStatus = 'active' | 'planning' | 'paused' | 'completed' | 'archived';

export const useUpdateCampaignStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ campaignId, status }: { campaignId: string; status: CampaignStatus }) => {
      const { error } = await supabase
        .from('campaigns')
        .update({ status })
        .eq('id', campaignId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-tracker-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-dashboard-metrics'] });
      queryClient.invalidateQueries({ queryKey: ['list-builder-campaigns'] });
    },
  });
};

// Recent connection interface
export interface RecentConnection {
  full_name: string | null;
  company_name: string | null;
  job_title: string | null;
  location: string | null;
  linkedin_url: string | null;
  connection_accepted_at: string | null;
  linkedin_sender_name: string | null;
  campaign_name: string | null;
  campaign_id: string | null;
  headline: string | null;
  was_already_connected: boolean | null;
  matched_contact_id: string | null;
}

// Hook to fetch recent connection acceptances
export const useRecentConnections = (limit: number = 10) => {
  return useQuery({
    queryKey: ['recent-connections', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_heyreach_recent_connections' as any)
        .select('*')
        .order('connection_accepted_at', { ascending: false, nullsFirst: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching recent connections:', error);
        return [];
      }

      return (data || []) as unknown as RecentConnection[];
    },
    staleTime: 2 * 60 * 1000,
  });
};

// Phase 7: parallel hook for Bison sync freshness tile
export const useLastBisonSync = () => {
  return useQuery({
    queryKey: ['last-bison-sync'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sync_log' as any)
        .select('completed_at, records_synced, sync_type')
        .eq('sync_source', 'emailbison')
        .is('error_message', null)
        .order('completed_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) return null;
      return data as unknown as { completed_at: string; records_synced: number; sync_type: string } | null;
    },
    staleTime: 60 * 1000,
  });
};

export const useLastHeyReachSync = () => {
  return useQuery({
    queryKey: ['last-heyreach-sync'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sync_log' as any)
        .select('completed_at, records_synced, sync_type')
        .eq('sync_source', 'heyreach')
        .order('completed_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as { completed_at: string; records_synced: number; sync_type: string } | null;
    },
  });
};

